import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, List, Grid, ListItem, Typography, ListItemText, Button } from '@material-ui/core';
import { useStyles, listTextStyles, customCardStyles } from './PromoCodeDetails.styled';
import ItemStatus from 'components/ItemStatus';
import { withRouter } from 'react-router-dom';
import { Card } from 'components/UI';
import i18n from 'i18n-js';
import Can from 'components/Can';
import { promoCodesActions } from 'state/modules/promoCodes';
import { forceTimezone, formatAmount } from 'utils';
import moment from 'moment';
import { UpdatePromoCodeModal } from 'components/Modals';

const PromoCodeDetails = ({ id }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const listClasses = listTextStyles();
  const cardClasses = customCardStyles();
  const [openUpdatePromoCodeModal, setOpenUpdatePromoCodeModal] = useState(false);
  const promoCode = useSelector((state) => state.promoCodes.promoCode);
  const localeFormat = useSelector((state) => state.organizations.localeFormat);

  const fetchPromoCodes = useCallback(
    (id) => {
      dispatch(promoCodesActions.fetchPromoCodeById(id));
    },
    [dispatch]
  );

  useEffect(() => {
    fetchPromoCodes(id);
  }, [fetchPromoCodes, id]);

  /**
   * Parse promo code data
   * @param {object} promoCode Object with promo code data
   */
  const getPromoCodeData = (promoCode = {}) => {
    const promoCodeInfo = [
      {
        label: `${i18n.t(`promo_codes.id`)}:`,
        value: `${promoCode?.code || '-'}`,
      },
      {
        label: `${i18n.t(`promo_codes.credit_amount`)}:`,
        value: `$${formatAmount(promoCode.amount || 0)}`,
      },
      {
        label: `${i18n.t(`promo_codes.start_time`)}:`,
        value: promoCode?.start_time
          ? forceTimezone(moment(promoCode?.start_time)).format(`${localeFormat}`)
          : '-',
      },
      {
        label: `${i18n.t(`promo_codes.expiration_time`)}:`,
        value: promoCode?.expiration_time
          ? forceTimezone(moment(promoCode?.expiration_time)).format(`${localeFormat}`)
          : '-',
      },
      {
        label: `${i18n.t(`common.description`)}:`,
        value: promoCode?.description || '-',
        classes: 'fullWidth',
      },
    ];

    return promoCodeInfo;
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [];
  /**
   * Open update promo code modal
   */
  const handleUpdatePromoCodeModal = useCallback(() => {
    setOpenUpdatePromoCodeModal((prevState) => !prevState);
  }, []);

  return (
    <Card
      menuItems={items}
      menuPermissions={{
        action: 'update',
        subject: 'promotional_code',
        type: 'can',
      }}
      title={i18n.t('promo_codes.info')}
      headerActionType="vertical"
      contentRootClasses={cardClasses}
    >
      <Box className={classes.cardContentPadding}>
        <Grid container direction="row" alignItems="center" spacing={1}>
          <Grid item xs={8}>
            <Typography variant="h3">
              <Box className={classes.name}>ID# {id}</Box>
            </Typography>
          </Grid>
          <Grid item xs={4} className={`${classes.textRight}`}>
            <ItemStatus
              i18nKey="common"
              status={promoCode?.is_enabled ? i18n.t('common.enabled') : i18n.t('common.disabled')}
            />
          </Grid>
        </Grid>
        <Can I="update" a="promotional_code">
          <Grid container direction="row" alignItems="center" spacing={1}>
            <Grid item xs={12} className={`${classes.textRight}`}>
              <Typography>
                <Button
                  onClick={handleUpdatePromoCodeModal}
                  data-cy="update-info"
                  className={classes.promoCodeLink}
                  color="primary"
                >
                  {i18n.t('common.update_information')}
                </Button>
              </Typography>
            </Grid>
          </Grid>
        </Can>

        <List className={classes.promoCodeList}>
          {getPromoCodeData(promoCode).map((val, index) => (
            <ListItem key={index} className={classes.promoCodeListItem}>
              <ListItemText
                classes={{
                  root: val.classes || listClasses.root,
                  primary: listClasses.primary,
                  secondary: listClasses.secondary,
                }}
                primary={val.label}
                secondary={
                  <Box
                    display="flex"
                    component="span"
                    flexDirection="column"
                    className={!val.classes ? classes.textRight : ''}
                  >
                    <Typography variant="body2" component="span" color="textSecondary">
                      {val.value}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>

        {openUpdatePromoCodeModal && (
          <UpdatePromoCodeModal
            open={openUpdatePromoCodeModal}
            id={id}
            handleCancel={handleUpdatePromoCodeModal}
          />
        )}
      </Box>
    </Card>
  );
};

export default withRouter(PromoCodeDetails);
